import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { BRAND_ASSETS } from "@/lib/brandAssets";

const LOGO_URL = BRAND_ASSETS.logo;

export default function AdminSetup() {
  const [setupToken, setSetupToken] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/admin/status`).then(r => {
      if (r.data.exists) navigate("/admin/login");
    }).catch(() => {});
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || !name) { toast.error("All fields are required"); return; }
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirmPassword) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/setup`, { email, password, name, setup_token: setupToken });
      login(res.data.access_token, res.data.admin);
      toast.success("Admin account created!");
      navigate("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Setup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center px-4">
      <Card className="w-full max-w-md border border-gray-800 shadow-2xl bg-[#1a1a1a]">
        <CardContent className="p-6 sm:p-8">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Junk In Da Truck" className="h-20 w-auto mx-auto mb-4 rounded-xl" loading="lazy" />
            <h1 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Create Admin Account</h1>
            <p className="text-sm text-gray-400 mt-1">First-time setup for your management portal</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Label htmlFor="setup-token">Provisioning token</Label>
            <Input id="setup-token" type="password" required value={setupToken} onChange={e => setSetupToken(e.target.value)} />
            <div>
              <Label className="text-gray-300">Your Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="D Angelo Hoover" className="bg-[#0D0D0D] border-gray-700 text-white placeholder:text-gray-500" />
            </div>
            <div>
              <Label className="text-gray-300">Email</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@junkindatruck.com" className="bg-[#0D0D0D] border-gray-700 text-white placeholder:text-gray-500" />
            </div>
            <div>
              <Label className="text-gray-300">Password (min 8 characters)</Label>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Create a strong password" className="bg-[#0D0D0D] border-gray-700 text-white placeholder:text-gray-500" />
            </div>
            <div>
              <Label className="text-gray-300">Confirm Password</Label>
              <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm your password" className="bg-[#0D0D0D] border-gray-700 text-white placeholder:text-gray-500" />
            </div>
            <Button type="submit" className="w-full bg-[#97BF04] text-[#0D0D0D] hover:bg-[#83A603] font-semibold gap-2" disabled={loading}>
              {loading ? "Creating..." : "Create Admin Account"}
              {!loading && <UserPlus className="w-4 h-4" />}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
